
import Vue, { PropType } from 'vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import pluralize from 'pluralize';
import cloneDeep from 'lodash/cloneDeep';
import _keys from 'lodash/keys';
import { v4 as uuidv4 } from 'uuid';
import { db, functions } from '@/firebase';
import { mapFirebaseTimestampToDate } from '@/utils/timeUtils';
import BaseDrawerOrBottomSheet from '@/components/base/BaseDrawerOrBottomSheet.vue';
import BaseButton from '@/components/base/BaseButton.vue';
import WindowStepsPanel, { WindowStepItem } from '@/components/WindowStepsPanel.vue';
import WindowStepper from '@/components/WindowStepper.vue';
import RunnitNodeFieldOptionCard from '@/views/Runnits/RunnitNodeFieldOptionCard.vue';
import AutoSaveRunnitNodeRunContainer from '@/views/Runnits/RunnitSettings/AutoSaveRunnitNodeRunContainer.vue';
import RunnitDynamicField from '@/views/Runnits/RunnitSettings/RunnitDynamicField.vue';
import RunnitBatchProcessingProgressCard, { BatchProgressRow } from '@/views/Runnits/RunnitBatchProcessing/RunnitBatchProcessingProgressCard.vue';
import RunnitImage from '@/views/Runnits/RunnitImage.vue';
import ConfirmDialog from '@/components/base/ConfirmDialog.vue';
import ImageInfoFields from '@/components/ImageGallery/ImageInfoFields.vue';
import ImageInputField from '@/views/Runnits/RunnitSettings/PromptDrivenForm/MainActionFields/ImageInputField.vue';
import MultiImageInputField from '@/views/Runnits/RunnitSettings/PromptDrivenForm/MainActionFields/MultiImageInputField.vue';
import VideoInputField from '@/views/Runnits/RunnitSettings/PromptDrivenForm/MainActionFields/VideoInputField.vue';
import Asset3DInputField from '@/views/Runnits/RunnitSettings/PromptDrivenForm/MainActionFields/Asset3DInputField.vue';
import {
	RunnitNode,
	RunnitNodeField,
	RunnitNodeFieldType,
	RunnitNodeRun,
	RunnitNodeRunResult,
	RunnitUpload,
	RUNNIT_NODE_STATIC_FIELDS_KEY,
	RUNNIT_NODE_DEF_PRICING_TYPE,
	getRunnitNodeRunPrice,
	hundredthsToTokens,
	currencyTokens,
	PROMPT_MAGIC_MODE,
	PromptMagicMode,
	RUNNIT_NODE_FIELD_TYPE,
	IMAGE_UPLOAD_MODE,
	RUNNIT_NODE_RUN_MODE,
	RUNNIT_NODE_RUN_STATE,
} from '@run-diffusion/shared';
import { NODE_RUN_RESULT_SELECTION_MODE } from '@/views/Runnits/constants';
import { IMAGE_GALLERY_DIALOG_NAV } from '@/components/ImageGallery/utils';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import ImageUpload from '@/views/Runnits/RunnitSettings/ImageInput/ImageUpload.vue';
import VideoUpload from '@/views/Runnits/RunnitSettings/ImageInput/VideoUpload.vue';
import {
	BATCH_MEDIA_VARIABLE_FIELD_MODE,
	batchBaseSettingsFields,
	collectBatchVariableFieldCandidates,
	getBatchMediaVariableFieldMode,
	isNodeBatchEligible,
} from '@/utils/runnitBatchVariableFields';
import { initiateFileDownload } from '@/utils/file-io';
import { parseCsv } from '@/utils/csv';
import { RUNNITS_OWNER_SELECTION } from '@/views/Runnits/constants';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { TeamUserMixin } from '@/mixins';

type PromptVariableFieldDefault = { value: string; magicMode: PromptMagicMode | null };

function defaultValueForVariableField (field: RunnitNodeField, user: { runnitSettings?: { promptMagicDefaultOff?: boolean } } | null): PromptVariableFieldDefault | null {
	const t = field.type;
	if (t === RUNNIT_NODE_FIELD_TYPE.PROMPT || t === RUNNIT_NODE_FIELD_TYPE.NEG_PROMPT) {
		return {
			value: '',
			magicMode: user?.runnitSettings?.promptMagicDefaultOff ? PROMPT_MAGIC_MODE.OFF : null,
		};
	}
	return null;
}

const PROMPT_CSV_MAX_BYTES = 5 * 1024 * 1024;

function mapLibrarySelectionToVariationRows (
	fieldType: RunnitNodeFieldType,
	nodeRunResults: RunnitNodeRunResult[],
	uploads: RunnitUpload[],
): unknown[] {
	const rows: unknown[] = [];
	for (const nr of nodeRunResults) {
		if (fieldType === RUNNIT_NODE_FIELD_TYPE.IMGS) {
			rows.push({ nodeRunResults: [nr] });
		} else {
			rows.push({ nodeRunResult: nr });
		}
	}
	for (const u of uploads) {
		if (fieldType === RUNNIT_NODE_FIELD_TYPE.IMGS) {
			rows.push({ uploads: [u] });
		} else {
			rows.push({ upload: u });
		}
	}
	return rows;
}

function mapComputerUploadPayloadToVariationRows (
	fieldType: RunnitNodeFieldType,
	payload: unknown,
): unknown[] {
	const raw = Array.isArray(payload) ? payload : [payload];
	const rows: unknown[] = [];
	for (const item of raw) {
		if (!item) continue;
		if (typeof item === 'object' && item !== null && 'nodeRunResult' in item && (item as { nodeRunResult?: unknown }).nodeRunResult) {
			const nr = (item as { nodeRunResult: RunnitNodeRunResult }).nodeRunResult;
			if (fieldType === RUNNIT_NODE_FIELD_TYPE.IMGS) {
				rows.push({ nodeRunResults: [nr] });
			} else {
				rows.push({ nodeRunResult: nr });
			}
		} else if (typeof item === 'object' && item !== null && 'upload' in item && (item as { upload?: unknown }).upload) {
			const u = (item as { upload: RunnitUpload }).upload;
			if (fieldType === RUNNIT_NODE_FIELD_TYPE.IMGS) {
				rows.push({ uploads: [u] });
			} else {
				rows.push({ upload: u });
			}
		} else {
			if (fieldType === RUNNIT_NODE_FIELD_TYPE.IMGS) {
				rows.push({ uploads: [item as RunnitUpload] });
			} else {
				rows.push({ upload: item as RunnitUpload });
			}
		}
	}
	return rows;
}

/** 0-based wizard step index (matches `WindowStepsPanel` order; v-stepper uses `wizardStep + 1` for `:step`). */
const BATCH_GEN_WIZARD_STEP = {
	VARIABLE: 0,
	VARIATIONS: 1,
	BASE_SETTINGS: 2,
	REVIEW: 3,
} as const;

export default Vue.extend({
	name: 'RunnitBatchGenerationDrawer',
	mixins: [TeamUserMixin, RunnitsImageSelectMixin],
	components: {
		BaseDrawerOrBottomSheet,
		BaseButton,
		WindowStepsPanel,
		WindowStepper,
		RunnitNodeFieldOptionCard,
		AutoSaveRunnitNodeRunContainer,
		RunnitDynamicField,
		RunnitBatchProcessingProgressCard,
		RunnitImage,
		ConfirmDialog,
		ImageInfoFields,
		ImageUpload,
		VideoUpload,
		ImageInputField,
		MultiImageInputField,
		VideoInputField,
		Asset3DInputField,
	},
	props: {
		value: { type: Boolean, default: false },
		selectedNode: { type: Object as PropType<RunnitNode | null>, default: null },
	},
	data () {
		return {
			BATCH_GEN_WIZARD_STEP,
			IMAGE_UPLOAD_MODE,
			/** Increment to remount the drawer when the user cancels "close while running" so internal `isOpen` resyncs with `value`. */
			drawerResyncKey: 0,
			wizardStep: BATCH_GEN_WIZARD_STEP.VARIABLE as number,
			uiMode: 'wizard' as 'wizard' | 'generating' | 'complete',
			selectedVariableFieldDefUuid: null as string | null,
			variationRowValues: [] as unknown[],
			incrementAutoSaveTrigger: 0,
			confirmRunOpen: false,
			closeWhileRunningDialogOpen: false,
			closeWhileRunningConfirmResolve: null as ((didConfirm: boolean) => void) | null,
			currentBatchId: null as string | null,
			progressRows: [] as BatchProgressRow[],
			batchCancelRequested: false,
			reviewBaseInputSnapshot: {} as Record<string, unknown>,
			reviewStaticSnapshot: {} as Record<string, unknown>,
			beforeUnloadHandler: null as ((e: BeforeUnloadEvent) => void) | null,
			maxVariations: 100,
			batchMediaLibrarySessionActive: false,
			batchMediaLibraryFieldDefUuid: null as string | null,
			/** Row values before "Select from left" so we can live-sync the grid and roll back on drawer close. */
			batchMediaSelectionRowsAtModeEnter: null as unknown[] | null,
			asset3DAddInputKey: 0,
		};
	},
	computed: {
		...mapState([
			'user',
			'team',
			'runnitState',
			'draftRunnitNodeRun',
			'loadingDraftRunnitNodeRun',
		]),
		...mapGetters([
			'displayLabels',
		]),
		// A deactivated team keeps its past generations but can't run new ones (runRunnitNode
		// refuses them too), so a batch can't start
		isTeamDeactivated (): boolean {
			return !!(
				this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM &&
				this.team &&
				!this.isTeamActive
			);
		},
		teamDeactivatedMessage (): string {
			return `This ${this.displayLabels.team} is deactivated, so it can't run new generations.`;
		},
		inputValues (): Record<string, unknown> {
			return this.runnitState.inputValues || {};
		},
		staticInputValues (): Record<string, unknown> {
			return this.runnitState.staticInputValues || {};
		},
		nodeDefTitle (): string {
			const n = this.selectedNode;
			if (!n || typeof n.nodeDef === 'string') return '';
			return n.nodeDef.title || '';
		},
		nodeDefAvatar (): unknown {
			const n = this.selectedNode;
			if (!n || typeof n.nodeDef === 'string') return null;
			return n.nodeDef.avatar || null;
		},
		determinedStaticFields () {
			const n = this.selectedNode;
			if (!n) return {};
			return typeof n.nodeDef !== 'string'
				? n.nodeDef.staticFields
				: {};
		},
		nodeFieldsForPricing () {
			const n = this.selectedNode;
			if (!n || typeof n.nodeDef === 'string') return [];
			return n.nodeDef.fields || [];
		},
		variableCandidates (): RunnitNodeField[] {
			if (!this.selectedNode) return [];
			return collectBatchVariableFieldCandidates(
				this.selectedNode,
				this.inputValues as any,
				!!this.user?.isAdmin,
			);
		},
		canOpenBatchWizard (): boolean {
			return isNodeBatchEligible(this.selectedNode) && this.hasBatchGenerationPermission;
		},
		selectedVariableField (): RunnitNodeField | null {
			if (!this.selectedVariableFieldDefUuid) return null;
			return this.variableCandidates.find((f) => f.fieldDefUuid === this.selectedVariableFieldDefUuid) || null;
		},
		baseSettingsFields (): RunnitNodeField[] {
			if (!this.selectedNode) return [];
			return batchBaseSettingsFields(
				this.selectedNode,
				this.inputValues as any,
				!!this.user?.isAdmin,
				this.selectedVariableFieldDefUuid,
			);
		},
		panelSteps (): WindowStepItem[] {
			const sub = this.selectedVariableField?.label || '';
			return [
				{ id: 'v', label: 'Variable', subtitle: sub },
				{ id: 'var', label: 'Variations' },
				{ id: 'b', label: 'Base Settings' },
				{ id: 'r', label: 'Review' },
			];
		},
		panelMode (): 'wizard' | 'generating' | 'complete' {
			if (this.uiMode === 'generating') return 'generating';
			if (this.uiMode === 'complete') return 'complete';
			return 'wizard';
		},
		/** Hide ActionIsland on Variable step only; selecting a field advances to Variations. */
		batchGenShowActionsIsland (): boolean {
			if (this.uiMode !== 'wizard') return true;
			return this.wizardStep !== BATCH_GEN_WIZARD_STEP.VARIABLE;
		},
		batchGenScrollAreaPadClass (): string {
			if (this.uiMode === 'wizard' && this.wizardStep === BATCH_GEN_WIZARD_STEP.VARIABLE) {
				return 'batch-gen-scroll-area--no-bottom-bar';
			}
			return 'batch-gen-scroll-area--with-bottom-bar';
		},
		continueDisabled (): boolean {
			if (this.wizardStep === BATCH_GEN_WIZARD_STEP.VARIABLE) return !this.selectedVariableFieldDefUuid;
			if (this.wizardStep === BATCH_GEN_WIZARD_STEP.VARIATIONS) return this.variationRowValues.length === 0;
			return false;
		},
		startDisabled (): boolean {
			return (
				this.isTeamDeactivated ||
				!this.draftRunnitNodeRun?.id ||
				this.loadingDraftRunnitNodeRun ||
				this.variationRowValues.length === 0 ||
				!this.hasBatchGenerationPermission ||
				this.exceedsTokenLimit
			);
		},
		/** Sum of `getRunnitNodeRunPrice` per variation row (same units as RunRunnitButton: hundredths). */
		estimatedTotalHundredths (): number {
			const node = this.selectedNode;
			const uuid = this.selectedVariableFieldDefUuid;
			if (
				!node ||
				typeof node.nodeDef === 'string' ||
				!uuid ||
				node.nodeDef.pricingType !== RUNNIT_NODE_DEF_PRICING_TYPE.COST ||
				!node.nodeDef.costPerResult
			) {
				return 0;
			}
			const fields = this.nodeFieldsForPricing;
			let sum = 0;
			for (const row of this.variationRowValues) {
				const merged = { ...this.inputValues, [uuid]: row } as any;
				sum += getRunnitNodeRunPrice(
					node.nodeDef.costPerResult,
					1,
					node.nodeDef.costPerResultLogics,
					merged,
					fields,
				);
			}
			return sum;
		},
		/** Display tokens — match RunRunnitButton (`hundredthsToTokens` then `currencyTokens`). */
		formattedEstimatedTotal (): string {
			const h = this.estimatedTotalHundredths;
			if (!h) return '0';
			return currencyTokens(hundredthsToTokens(h));
		},
		tokenLimitMax (): number | null {
			if (!this.hasTokenLimitPermission) return null;
			// A batch runs one tool many times, so the cap is that tool's: the
			// role's per-media-type override when the tool carries that MEDIA tag,
			// else the master cap.
			const n = this.selectedNode;
			const nodeDef = (n && typeof n.nodeDef !== 'string') ? n.nodeDef : null;
			return this.getTokenLimitMaxForNodeDef(this.team, this.teamUserRole, nodeDef);
		},
		tokenLimitAppliesToTotal (): boolean {
			if (!this.hasTokenLimitPermission) return false;
			return !!this.teamUserRole?.roleDef?.tokenLimitSettings?.applyToTotalRun;
		},
		exceedsTokenLimit (): boolean {
			const max = this.tokenLimitMax;
			if (!max) return false;
			
			if (this.tokenLimitAppliesToTotal) {
				return this.estimatedTotalHundredths > max;
			} else {
				if (this.variationRowValues.length === 0) return false;
				const maxSingleCost = this.estimatedTotalHundredths / this.variationRowValues.length;
				return maxSingleCost > max;
			}
		},
		tokenLimitWarningText (): string {
			if (!this.exceedsTokenLimit) return '';
			const maxFormatted = currencyTokens(hundredthsToTokens(this.tokenLimitMax));
			if (this.tokenLimitAppliesToTotal) {
				return `This batch exceeds your total token limit of ${maxFormatted} tokens.`;
			} else {
				return `The generations in this batch exceed your per-generation token limit of ${maxFormatted} tokens.`;
			}
		},
		batchConfirmBodyText (): string {
			const n = this.variationRowValues.length;
			if (this.estimatedTotalHundredths > 0) {
				return `Total estimated cost is ${this.formattedEstimatedTotal} tokens for ${n} runs. Make sure you are ready to use that many tokens before continuing.`;
			}
			return `${n} run(s) will be submitted. Continue?`;
		},
		progressFraction (): number {
			if (!this.progressRows.length) return 0;
			const done = this.progressRows.filter((r) => r.state === 'initialized' || r.state === 'failed').length;
			return done / this.progressRows.length;
		},
		batchMediaSelectionTriggerId (): string {
			if (!this.selectedVariableFieldDefUuid) return 'batch-gen-media';
			return `batch-gen-media-${this.selectedVariableFieldDefUuid}`;
		},
		isBatchMediaSelectionActive (): boolean {
			return this.currentSelectionState?.triggeringId === this.batchMediaSelectionTriggerId;
		},
		/** Count of library / upload picks in the left-panel multi-selection for batch media (node run results + uploads). */
		batchMediaLeftSelectionCount (): number {
			if (this.batchMediaSelectionRowsAtModeEnter == null) return 0;
			if (!this.isBatchMediaSelectionActive) return 0;
			const st = this.getSelectionStateByTrigger(this.batchMediaSelectionTriggerId);
			if (!st?.sortOrder) return 0;
			const nrr = st.selectedNodeRunResults || [];
			const upl = st.selectedUploads || [];
			return nrr.length + upl.length;
		},
		/**
		 * Identity of the current left-panel picks for batch media live-sync only.
		 * Avoids deep-watching all of `selectionStateHistory` (which fires on unrelated selection updates).
		 */
		batchMediaSelectionFingerprint (): string {
			if (this.batchMediaSelectionRowsAtModeEnter == null) return '';
			if (!this.isBatchMediaSelectionActive) return '';
			const st = this.getSelectionStateByTrigger(this.batchMediaSelectionTriggerId);
			if (!st?.sortOrder) return '';
			const nrr = st.selectedNodeRunResults || [];
			const upl = st.selectedUploads || [];
			return [
				...nrr.map((row) => row.uuid),
				...upl.map((u) => `u:${u.id}`),
			].join('\n');
		},
		isMediaVariableField (): boolean {
			return !!getBatchMediaVariableFieldMode(this.selectedVariableField?.type);
		},
		isPromptVariableField (): boolean {
			const t = this.selectedVariableField?.type;
			return t === RUNNIT_NODE_FIELD_TYPE.PROMPT || t === RUNNIT_NODE_FIELD_TYPE.NEG_PROMPT;
		},
		isImageLikeMediaVariableField (): boolean {
			return getBatchMediaVariableFieldMode(this.selectedVariableField?.type) === BATCH_MEDIA_VARIABLE_FIELD_MODE.image;
		},
		isVideoMediaVariableField (): boolean {
			return getBatchMediaVariableFieldMode(this.selectedVariableField?.type) === BATCH_MEDIA_VARIABLE_FIELD_MODE.video;
		},
		isAsset3DMediaVariableField (): boolean {
			return getBatchMediaVariableFieldMode(this.selectedVariableField?.type) === BATCH_MEDIA_VARIABLE_FIELD_MODE.asset3d;
		},
		canShowAsset3DAddVariation (): boolean {
			return this.isAsset3DMediaVariableField &&
				!this.isBatchMediaSelectionActive &&
				this.variationRowValues.length < this.maxVariations;
		},
		asset3DAddVariationField (): RunnitNodeField | null {
			const field = this.selectedVariableField;
			if (!field) return null;
			return {
				...field,
				uuid: `${field.uuid}-batch-asset-3d-add-${this.asset3DAddInputKey}`,
			};
		},
		/** Clone of draft run + given inputs for readonly `ImageInfoFields` (avoids mutating store). */
		fakeBaseSettingsNodeRun (): RunnitNodeRun | null {
			return this.reviewNodeRunWithInputs(this.inputValues as Record<string, unknown>);
		},
		/** One `ImageInfoFields` nodeRun + field per variation row (single clone per row). */
		variationReviewRows (): { idx: number; field: RunnitNodeField; nodeRun: RunnitNodeRun }[] {
			const out: { idx: number; field: RunnitNodeField; nodeRun: RunnitNodeRun }[] = [];
			this.variationRowValues.forEach((rowVal, idx) => {
				const field = this.variationRowField(idx);
				const nodeRun = this.variationReviewNodeRun(rowVal);
				if (field && nodeRun) {
					out.push({ idx, field, nodeRun });
				}
			});
			return out;
		},
	},
	watch: {
		batchMediaSelectionFingerprint () {
			this.applyLiveBatchMediaSelectionToRows();
		},
		value (open: boolean) {
			if (open) {
				if (!this.canOpenBatchWizard) {
					this.rejectDrawerOpen();
					return;
				}
				this.onOpen();
			} else {
				this.onBatchLeftSelectAbandonOnClose();
			}
		},
		canOpenBatchWizard (canOpen: boolean) {
			if (!canOpen && this.value) {
				this.rejectDrawerOpen();
			}
		},
		selectedVariableFieldDefUuid () {
			this.initVariationRowsFromSelection();
		},
		'runnitState.imageGalleryDialogOpen': {
			handler (newVal: boolean, oldVal: boolean) {
				if (newVal === false && oldVal === true && this.batchMediaLibrarySessionActive) {
					this.consumeBatchMediaLibrarySelection();
				}
			},
		},
		batchCancelRequested (cancel: boolean) {
			if (cancel && this.uiMode === 'generating') {
				this.abortBatchGeneration();
			}
		},
	},
	methods: {
		...mapActions(['updateRunnitState', 'updateSnackbar']),
		reviewNodeRunWithInputs (mergedInputs: Record<string, unknown>): RunnitNodeRun | null {
			const draft = this.draftRunnitNodeRun as RunnitNodeRun | null | undefined;
			if (!draft) return null;
			const nr = cloneDeep(draft);
			nr.inputs = cloneDeep(mergedInputs) as RunnitNodeRun['inputs'];
			return nr;
		},
		variationReviewNodeRun (rowVal: unknown): RunnitNodeRun | null {
			const uuid = this.selectedVariableFieldDefUuid;
			if (!uuid) return null;
			return this.reviewNodeRunWithInputs({
				...this.inputValues,
				[uuid]: rowVal,
			} as Record<string, unknown>);
		},
		async onDrawerInput (v: boolean) {
			if (v) {
				// The drawer has no internal activator; opening must come from the parent.
				if (!this.value || !this.canOpenBatchWizard) {
					this.rejectDrawerOpen();
					return;
				}
				this.$emit('input', true);
				return;
			}
			if (!(await this.confirmCloseDuringRun())) {
				/* BaseDrawerOrBottomSheet already set internal `isOpen` false before this async handler ran; parent `value` may still be true, so the value watcher never resyncs. Remount restores `isOpen` from `value`. */
				this.drawerResyncKey++;
				return;
			}
			this.resetAfterClose();
			this.$emit('input', false);
		},
		rejectDrawerOpen () {
			this.resetAfterClose();
			this.drawerResyncKey++;
			this.$emit('input', false);
		},
		/** Resolves `true` if the user may close; `false` if they chose to stay (same idea as ManageTagsDialog `didConfirm`). */
		confirmCloseDuringRun (): Promise<boolean> {
			if (this.uiMode !== 'generating') return Promise.resolve(true);
			this.closeWhileRunningDialogOpen = true;
			return new Promise((resolve) => {
				this.closeWhileRunningConfirmResolve = (didConfirm: boolean) => {
					this.closeWhileRunningConfirmResolve = null;
					resolve(didConfirm);
				};
			});
		},
		onCloseWhileRunningDialogInput (open: boolean) {
			this.closeWhileRunningDialogOpen = open;
			if (!open && this.closeWhileRunningConfirmResolve) {
				this.closeWhileRunningConfirmResolve(false);
			}
		},
		onCloseWhileRunningDialogConfirm (closeDialog: () => void) {
			if (this.closeWhileRunningConfirmResolve) {
				const r = this.closeWhileRunningConfirmResolve;
				this.closeWhileRunningConfirmResolve = null;
				r(true);
			}
			closeDialog();
		},
		async closeFromToolbar (setOpen: (x: boolean) => void) {
			if (!(await this.confirmCloseDuringRun())) return;
			if (this.uiMode === 'generating') this.batchCancelRequested = true;
			setOpen(false);
			this.resetAfterClose();
			this.$emit('input', false);
		},
		onOpen () {
			this.wizardStep = BATCH_GEN_WIZARD_STEP.VARIABLE;
			this.uiMode = 'wizard';
			this.confirmRunOpen = false;
			this.currentBatchId = null;
			this.batchCancelRequested = false;
			this.batchMediaSelectionRowsAtModeEnter = null;
			this.progressRows = [];
			if (!this.selectedVariableFieldDefUuid && this.variableCandidates.length === 1) {
				this.selectedVariableFieldDefUuid = this.variableCandidates[0].fieldDefUuid;
			}
			this.initVariationRowsFromSelection();
			if (this.variableCandidates.length === 1 && this.selectedVariableFieldDefUuid) {
				this.wizardStep = BATCH_GEN_WIZARD_STEP.VARIATIONS;
			}
		},
		onVariableFieldCardSelect (fieldDefUuid: string) {
			this.selectedVariableFieldDefUuid = fieldDefUuid;
			this.wizardStep = BATCH_GEN_WIZARD_STEP.VARIATIONS;
		},
		initVariationRowsFromSelection () {
			const field = this.selectedVariableField;
			if (!field) {
				this.variationRowValues = [];
				return;
			}
			// Always start the Variations step with no rows; user adds via Add Variation / upload / library.
			this.variationRowValues = [];
		},
		variationRowInputValues (idx: number): Record<string, unknown> {
			const base = { ...this.inputValues } as Record<string, unknown>;
			const field = this.selectedVariableField;
			if (field) {
				base[field.fieldDefUuid] = this.variationRowValues[idx];
			}
			return base;
		},
		variationRowField (idx: number): RunnitNodeField | null {
			const field = this.selectedVariableField;
			if (!field) return null;
			return {
				...field,
				uuid: `${field.uuid}-batch-${idx}`,
			};
		},
		onVariationFieldInput (idx: number, payload: { field: RunnitNodeField; value: unknown } | unknown) {
			const p = payload as { field: RunnitNodeField; value: unknown };
			const next = [...this.variationRowValues];
			next[idx] = cloneDeep(p.value);
			this.variationRowValues = next;
		},
		onMediaVariationInput (idx: number, value: unknown) {
			if (!value) {
				this.removeVariationRow(idx);
			} else {
				this.onVariationFieldInput(idx, { field: this.variationRowField(idx) as RunnitNodeField, value });
			}
		},
		onAsset3DAddVariationInput (value: unknown) {
			if (!value || !this.isAsset3DMediaVariableField) return;
			this.appendVariationRows([value]);
			this.asset3DAddInputKey++;
		},
		onInputValuesInput (v: Record<string, unknown>) {
			this.updateRunnitState({ inputValues: v });
		},
		onStaticInputValuesInput (v: Record<string, unknown>) {
			this.updateRunnitState({ staticInputValues: v });
		},
		addVariationRow () {
			const field = this.selectedVariableField;
			if (!field || this.isMediaVariableField || this.variationRowValues.length >= this.maxVariations) return;
			this.variationRowValues = [
				...this.variationRowValues,
				defaultValueForVariableField(field, this.user),
			];
		},
		removeVariationRow (idx: number) {
			this.variationRowValues = this.variationRowValues.filter((_, i) => i !== idx);
		},
		toggleSelectFromLeft () {
			if (this.isBatchMediaSelectionActive) {
				// Commit: rows already match the left-panel selection (live); only exit and clear.
				this.exitSelectionMode(this.batchMediaSelectionTriggerId);
				this.clearSelectionsFromState(this.batchMediaSelectionTriggerId);
				this.batchMediaSelectionRowsAtModeEnter = null;
			} else {
				// Entering selection mode
				const field = this.selectedVariableField;
				if (!field || !this.isMediaVariableField || this.variationRowValues.length >= this.maxVariations) return;
				this.batchMediaSelectionRowsAtModeEnter = this.variationRowValues.map((r) => cloneDeep(r));
				this.batchMediaLibraryFieldDefUuid = field.fieldDefUuid;

				// Clear any previous selection state so we start fresh
				this.clearSelectionsFromState(this.batchMediaSelectionTriggerId);
				this.syncBatchMediaSelectionState();
				this.$nextTick(() => this.applyLiveBatchMediaSelectionToRows());
			}
		},
		/**
		 * While in "select from left", the variation grid is base rows + a capped mapping of the
		 * current runnit left-panel multi-selection, so items appear as the user checks them.
		 */
		applyLiveBatchMediaSelectionToRows () {
			if (this.batchMediaSelectionRowsAtModeEnter == null) return;
			if (!this.isBatchMediaSelectionActive) return;
			const field = this.selectedVariableField;
			if (!field) return;
			const state = this.getSelectionStateByTrigger(this.batchMediaSelectionTriggerId);
			if (!state?.sortOrder) return;
			const nodeRunResults = state.selectedNodeRunResults || [];
			const uploads = state.selectedUploads || [];
			const fromSelection = mapLibrarySelectionToVariationRows(field.type, nodeRunResults, uploads);
			const maxNew = Math.max(0, this.maxVariations - this.batchMediaSelectionRowsAtModeEnter.length);
			const tail = fromSelection.slice(0, maxNew);
			// Snapshot is frozen for this session; `mapLibrarySelectionToVariationRows` returns new row objects for the tail.
			const nextValues = [...this.batchMediaSelectionRowsAtModeEnter, ...tail];
			if (
				nextValues.length === this.variationRowValues.length
				&& nextValues.every((row, i) => row === this.variationRowValues[i])
			) {
				return;
			}
			this.variationRowValues = nextValues;
		},
		/**
		 * Drawer was closed (or unmounted) without using Confirm: restore the grid to how it was before "Select from left", and clear selection mode.
		 */
		onBatchLeftSelectAbandonOnClose () {
			if (this.batchMediaSelectionRowsAtModeEnter != null) {
				this.variationRowValues = [...this.batchMediaSelectionRowsAtModeEnter];
			}
			this.batchMediaSelectionRowsAtModeEnter = null;
			if (this.getSelectionStateByTrigger(this.batchMediaSelectionTriggerId)?.sortOrder) {
				this.clearSelectionsFromState(this.batchMediaSelectionTriggerId);
				this.exitSelectionMode(this.batchMediaSelectionTriggerId);
			}
		},
		syncBatchMediaSelectionState () {
			const field = this.selectedVariableField;
			if (!field) return;
			const canSelectTypes =
				field.type === RUNNIT_NODE_FIELD_TYPE.VIDEO ? (['VID'] as const)
					: field.type === RUNNIT_NODE_FIELD_TYPE.ASSET_3D ? (['ASSET_3D'] as const)
						: (['IMG'] as const);
			this.updateSelectionState({
				triggeringId: this.batchMediaSelectionTriggerId,
				sortOrder: 0,
				nodeRunResultSelectionMode: NODE_RUN_RESULT_SELECTION_MODE.MULTIPLE,
				canSelectTypes: [...canSelectTypes],
				fieldAwaitingImageGallerySelection: field,
				selectedNodeRunsMap: {},
				selectedNodeRunResults: [],
				selectedLoadedAssets: [],
				selectedUploads: [],
				selectedAvatars: [],
			});
			this.enterSelectionMode(
				NODE_RUN_RESULT_SELECTION_MODE.MULTIPLE,
				this.batchMediaSelectionTriggerId,
				field,
				[...canSelectTypes],
			);
		},
		openBatchMediaLibrary () {
			const field = this.selectedVariableField;
			if (!field || !this.isMediaVariableField || this.variationRowValues.length >= this.maxVariations) return;
			this.batchMediaLibraryFieldDefUuid = field.fieldDefUuid;
			this.batchMediaLibrarySessionActive = true;
			this.clearSelectionsFromState(this.batchMediaSelectionTriggerId);
			this.syncBatchMediaSelectionState();
			this.updateRunnitState({
				imageGalleryDialogOpen: true,
				imageGalleryDialogInitialNav: IMAGE_GALLERY_DIALOG_NAV.GENERATIONS,
			});
		},
		consumeBatchMediaLibrarySelection () {
			const fieldDefUuid = this.batchMediaLibraryFieldDefUuid;
			this.batchMediaLibrarySessionActive = false;
			this.batchMediaLibraryFieldDefUuid = null;
			if (!fieldDefUuid) return;
			const triggerId = `batch-gen-media-${fieldDefUuid}`;
			const state = this.getSelectionStateByTrigger(triggerId);
			const nodeRunResults = state?.selectedNodeRunResults || [];
			const uploads = state?.selectedUploads || [];
			this.clearSelectionsFromState(triggerId);
			if (!nodeRunResults.length && !uploads.length) return;
			if (this.selectedVariableFieldDefUuid !== fieldDefUuid) return;
			const field = this.selectedVariableField;
			if (!field) return;
			const rows = mapLibrarySelectionToVariationRows(field.type, nodeRunResults, uploads);
			this.appendVariationRows(rows);
		},
		remainingVariationSlots (): number {
			return Math.max(0, this.maxVariations - this.variationRowValues.length);
		},
		appendVariationRows (rows: unknown[]) {
			if (!rows.length) return;
			const n = Math.min(rows.length, this.remainingVariationSlots());
			if (n <= 0) return;
			this.variationRowValues = [...this.variationRowValues, ...rows.slice(0, n).map((r) => cloneDeep(r))];
		},
		downloadPromptCsvTemplate () {
			const isNeg = this.selectedVariableField?.type === RUNNIT_NODE_FIELD_TYPE.NEG_PROMPT;
			const header = isNeg ? 'negative_prompt' : 'prompt';
			const examples = isNeg
				? [
					'blurry, low quality',
					'extra fingers, deformed hands',
					'oversaturated, washed out',
					'text, watermark, signature',
					'cropped, out of frame',
				]
				: [
					'A serene mountain landscape at sunrise, photorealistic',
					'A cozy coffee shop interior with warm lighting, oil painting style',
					'A futuristic city skyline at night, cyberpunk aesthetic',
					'A close-up portrait of a wise old wizard, fantasy art',
					'A tropical beach with palm trees and crystal clear water',
				];
			const escape = (v: string) => {
				const needsQuoting = v.includes(',') || v.includes('"') || v.includes('\n') || v.includes('\r');
				if (!needsQuoting) return v;
				return '"' + v.replace(/"/g, '""') + '"';
			};
			const csv = [header, ...examples.map(escape)].join('\r\n');
			const filename = isNeg ? 'negative-prompt-batch-template.csv' : 'prompt-batch-template.csv';
			initiateFileDownload(csv, filename, 'text/csv');
		},
		openPromptCsvUpload () {
			if (this.variationRowValues.length >= this.maxVariations) return;
			const input = this.$refs.promptCsvInput as HTMLInputElement | undefined;
			if (input) input.click();
		},
		async onPromptCsvFileChange (event: Event) {
			const input = event.target as HTMLInputElement;
			const file = input.files?.[0];
			// Reset immediately so picking the same file again later still triggers `change`
			input.value = '';
			if (!file) return;
			if (!this.isPromptVariableField) return;

			if (file.size > PROMPT_CSV_MAX_BYTES) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'CSV file is too large. Please upload a file under 5 MB.',
					show: true,
				});
				return;
			}

			let text: string;
			try {
				text = await file.text();
			} catch {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Could not read the CSV file. Please try again.',
					show: true,
				});
				return;
			}

			const grid = parseCsv(text);
			if (!grid.length) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'The CSV file is empty.',
					show: true,
				});
				return;
			}

			// Header row determines column count. A header with > 1 cell means a multi-column CSV → reject.
			// A header with 1 cell means a single-column CSV; data rows with embedded commas may have parsed
			// into multiple cells (unquoted commas in user-typed CSVs), so we re-join them with `,`.
			if (grid[0].length > 1) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Please only upload a CSV with just 1 column. Use the provided template as a reference.',
					show: true,
				});
				return;
			}

			const dataRows = grid.slice(1)
				.map((r) => r.join(',').trim())
				.filter((s) => s.length > 0);

			if (!dataRows.length) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'No prompts found in the CSV. Use the provided template as a reference.',
					show: true,
				});
				return;
			}

			const total = this.variationRowValues.length + dataRows.length;
			if (total > this.maxVariations) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: `Total prompts cannot exceed ${this.maxVariations}. You currently have ${this.variationRowValues.length} and this CSV would add ${dataRows.length}.`,
					show: true,
				});
				return;
			}

			const field = this.selectedVariableField;
			if (!field) return;
			const base = defaultValueForVariableField(field, this.user);
			const newRows = dataRows.map((value) => ({ ...(base || {}), value }));
			this.appendVariationRows(newRows);
			this.updateSnackbar({
				status: SNACKBAR_STATUS.SUCCESS,
				message: `Added ${dataRows.length} ${pluralize('prompt', dataRows.length)} from CSV.`,
				show: true,
			});
		},
		openBatchComputerUpload () {
			if (!this.hasGenerateRunnitPermission) return;
			const field = this.selectedVariableField;
			if (!field || !this.isMediaVariableField || this.variationRowValues.length >= this.maxVariations) return;
			this.$nextTick(() => {
				if (field.type === RUNNIT_NODE_FIELD_TYPE.VIDEO) {
					const ref = this.$refs.batchVideoUpload as { openFileDialog?: () => void } | undefined;
					ref?.openFileDialog?.();
				} else if (field.type === RUNNIT_NODE_FIELD_TYPE.ASSET_3D) {
					const ref = this.$refs.batchAsset3DAddInput as { openFileDialog?: () => void } | undefined;
					ref?.openFileDialog?.();
				} else {
					const ref = this.$refs.batchImageUpload as { openFileDialog?: () => void } | undefined;
					ref?.openFileDialog?.();
				}
			});
		},
		onBatchComputerUploadComplete (payload: unknown) {
			const field = this.selectedVariableField;
			if (!field || !this.isMediaVariableField) return;
			const rows = mapComputerUploadPayloadToVariationRows(field.type, payload);
			this.appendVariationRows(rows);
		},
		onContinue () {
			if (this.continueDisabled) return;
			this.wizardStep = Math.min(BATCH_GEN_WIZARD_STEP.REVIEW, this.wizardStep + 1);
		},
		openConfirmRun () {
			if (this.startDisabled) return;
			this.confirmRunOpen = true;
		},
		onBatchStartConfirm (closeDialog: () => void) {
			this.confirmAndRun();
			closeDialog();
		},
		confirmAndRun () {
			if (!this.hasBatchGenerationPermission) {
				this.confirmRunOpen = false;
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Your role does not allow batch generation.',
					show: true,
				});
				return;
			}
			this.confirmRunOpen = false;
			const base = cloneDeep(this.inputValues) as Record<string, unknown>;
			if (this.selectedVariableFieldDefUuid) {
				delete base[this.selectedVariableFieldDefUuid];
			}
			this.reviewBaseInputSnapshot = base;
			this.reviewStaticSnapshot = cloneDeep(this.staticInputValues);
			this.runBatch();
		},
		attachBeforeUnload () {
			this.detachBeforeUnload();
			this.beforeUnloadHandler = (e: BeforeUnloadEvent) => {
				e.preventDefault();
				e.returnValue = '';
			};
			window.addEventListener('beforeunload', this.beforeUnloadHandler);
		},
		detachBeforeUnload () {
			if (this.beforeUnloadHandler) {
				window.removeEventListener('beforeunload', this.beforeUnloadHandler);
				this.beforeUnloadHandler = null;
			}
		},
		/** Firestore-safe field maps (no `undefined`; timestamps → Date) — mirrors AutoSave draft writes. */
		mapRecordForFirestore (obj: Record<string, unknown>): Record<string, unknown> {
			const mapped: Record<string, unknown> = {};
			_keys(obj).forEach((key) => {
				const v = mapFirebaseTimestampToDate(obj[key]);
				mapped[key] = v === undefined ? null : v;
			});
			return mapped;
		},
		/**
		 * Each `runRunnitNode` consumes its draft doc. Batch index 0 uses the live draft; this creates
		 * a new draft for another variation (same shape as AutoSave `.add`), immediately before that run.
		 */
		async createBatchDraftForVariation (
			merged: Record<string, unknown>,
			staticForDraft: Record<string, unknown>,
			node: RunnitNode,
		): Promise<string> {
			const nowDate = new Date();
			const mappedInputs = this.mapRecordForFirestore(merged);
			const ref = await db.collection('runnitNodeRunDrafts').add({
				createdAt: nowDate,
				deletedAt: null,
				draftAt: nowDate,
				userId: (this.user as { id: string }).id,
				runnitId: node.runnitId,
				nodeId: node.id,
				nodeDefId: node.nodeDefId,
				nodeDef: db.doc(`runnitNodeDefs/${node.nodeDefId}`),
				mode: RUNNIT_NODE_RUN_MODE.MANUAL,
				state: RUNNIT_NODE_RUN_STATE.DRAFT,
				inputs: mappedInputs,
				staticInputs: staticForDraft,
			});
			return ref.id;
		},
		abortBatchGeneration () {
			this.detachBeforeUnload();
			this.uiMode = 'complete';
			this.updateRunnitState({ isQueuingDraftRunnitNodeRun: false });
		},
		async runBatch () {
			if (!this.hasBatchGenerationPermission) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Your role does not allow batch generation.',
					show: true,
				});
				return;
			}
			const node = this.selectedNode;
			const total = this.variationRowValues.length;
			const uuid = this.selectedVariableFieldDefUuid;
			if (!node || !uuid || total === 0) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Batch is not ready to run.',
					show: true,
				});
				return;
			}

			const baseDraftId = this.draftRunnitNodeRun?.id;
			if (!baseDraftId || this.loadingDraftRunnitNodeRun) {
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: 'Draft is not ready yet. Try again in a moment.',
					show: true,
				});
				return;
			}

			this.uiMode = 'generating';
			this.currentBatchId = uuidv4();
			this.attachBeforeUnload();

			this.progressRows = this.variationRowValues.map((_, i) => ({
				index: i,
				label: `${i + 1} of ${total}`,
				state: 'pending' as const,
			}));

			/*
			 * Keep queuing true for the whole batch so AutoSaveRunnitNodeRunContainer skips `doSaveDraft`
			 * while we create extra `runnitNodeRunDrafts` (just-in-time per variation) and callables run.
			 */
			this.updateRunnitState({ isQueuingDraftRunnitNodeRun: true });

			const staticForCall: Record<string, unknown> = { ...this.reviewStaticSnapshot };
			if (this.determinedStaticFields[RUNNIT_NODE_STATIC_FIELDS_KEY.numResults]) {
				staticForCall[RUNNIT_NODE_STATIC_FIELDS_KEY.numResults] = 1;
			}

			const teamId =
				this.runnitState.runnitsOwnerSelection === RUNNITS_OWNER_SELECTION.TEAM && (this.team as { id?: string })?.id
					? (this.team as { id: string }).id
					: null;

			const mergedList: Record<string, unknown>[] = this.variationRowValues.map((rowVal) => ({
				...this.reviewBaseInputSnapshot,
				[uuid]: cloneDeep(rowVal),
			}));

			const staticForDraft = this.mapRecordForFirestore(staticForCall);

			const fn = functions.httpsCallable('runRunnitNode');
			const useDev = !!window.localStorage.getItem('RUNNIT_USE_DEV_SERVER');
			const batchId = this.currentBatchId;
			let batchErrorSnackShown = false;
			/*
			 * Run callables one-at-a-time. Parallel `httpsCallable` invocations have been observed to
			 * "drop" the middle request (UI stuck on Processing) while first/last complete — likely
			 * client/auth transport or Firebase channel behavior, not our draft id mapping.
			 *
			 * Extra drafts (indices 1..n-1) are created just-in-time before each call so the first
			 * variation can start immediately without waiting for every Firestore `.add()`.
			 */
			const staticPayload = cloneDeep(staticForCall);

			try {
				for (let index = 0; index < total; index++) {
					if (this.uiMode !== 'generating' || this.batchCancelRequested) break;

					this.$set(this.progressRows[index], 'state', 'processing');

					let nodeRunId: string;
					if (index === 0) {
						nodeRunId = baseDraftId;
					} else {
						try {
							nodeRunId = await this.createBatchDraftForVariation(
								mergedList[index],
								staticForDraft,
								node,
							);
						} catch (e) {
							console.error(e);
							if (this.uiMode !== 'generating' || this.batchCancelRequested) break;
							this.$set(this.progressRows[index], 'state', 'failed');
							if (!batchErrorSnackShown) {
								batchErrorSnackShown = true;
								this.updateSnackbar({
									status: SNACKBAR_STATUS.ERROR,
									message: 'Could not create a draft for this batch item.',
									show: true,
								});
							}
							continue;
						}
					}

					if (this.uiMode !== 'generating' || this.batchCancelRequested) break;

					try {
						const res = await fn({
							...(useDev && { useDevServer: true }),
							nodeRunId,
							inputValues: cloneDeep(mergedList[index]),
							staticInputValues: staticPayload,
							teamId,
							batchInfo: {
								batchId,
								index,
								total,
							},
						});
						if (this.uiMode !== 'generating') break;
						const data = res.data as { success?: boolean; message?: string; teamInactive?: boolean };
						if (!data.success) {
							this.$set(this.progressRows[index], 'state', 'failed');
							if (!batchErrorSnackShown) {
								batchErrorSnackShown = true;
								this.updateSnackbar({
									status: SNACKBAR_STATUS.ERROR,
									message: data.message || 'Run failed',
									show: true,
								});
							}
							// A deactivated team can't run the rest either: stop instead of asking for each
							if (data.teamInactive) {
								for (let rest = index + 1; rest < total; rest++) {
									this.$set(this.progressRows[rest], 'state', 'failed');
								}
								break;
							}
							continue;
						}
						this.$set(this.progressRows[index], 'state', 'initialized');
					} catch (e: unknown) {
						console.error(e);
						if (this.uiMode === 'generating') {
							this.$set(this.progressRows[index], 'state', 'failed');
							if (!batchErrorSnackShown) {
								batchErrorSnackShown = true;
								const msg = e instanceof Error ? e.message : '';
								this.updateSnackbar({
									status: SNACKBAR_STATUS.ERROR,
									message: msg ? `Batch run error: ${msg}` : 'A batch run failed.',
									show: true,
								});
							}
						}
					}
				}
			} finally {
				this.updateRunnitState({ isQueuingDraftRunnitNodeRun: false });
				if (this.uiMode === 'generating') {
					this.detachBeforeUnload();
					this.uiMode = 'complete';
				}
			}
		},
		closeDrawer () {
			this.resetAfterClose();
			this.$emit('input', false);
		},
		async requestClose (): Promise<boolean> {
			if (!(await this.confirmCloseDuringRun())) return false;
			if (this.uiMode === 'generating') this.batchCancelRequested = true;
			this.resetAfterClose();
			this.$emit('input', false);
			return true;
		},
		resetAfterClose () {
			if (this.batchCancelRequested) {
				this.updateRunnitState({ isQueuingDraftRunnitNodeRun: false });
			}
			this.wizardStep = BATCH_GEN_WIZARD_STEP.VARIABLE;
			this.uiMode = 'wizard';
			this.detachBeforeUnload();
		},
	},
	beforeDestroy () {
		this.detachBeforeUnload();
		this.onBatchLeftSelectAbandonOnClose();
	},
});
